import { useRef, useState, useCallback, useEffect } from "react";
import { GripVertical } from "lucide-react";

/**
 * Before/After image comparison slider.
 * Props:
 *  - beforeSrc, afterSrc, beforeLabel, afterLabel
 *  - className, height (tailwind class for min-h)
 *  - bgChecker (boolean) - show transparent checker pattern background
 */
export default function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeLabel = "Before",
  afterLabel = "After",
  className = "",
  bgChecker = true,
}) {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(50);
  const [dragging, setDragging] = useState(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPosition(pct);
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    updateFromClientX(e.clientX);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const cx = e.clientX ?? e.touches?.[0]?.clientX;
      if (cx != null) updateFromClientX(cx);
    };
    const onUp = () => setDragging(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, updateFromClientX]);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPosition((p) => Math.max(0, p - 2));
    if (e.key === "ArrowRight") setPosition((p) => Math.min(100, p + 2));
    if (e.key === "Home") setPosition(0);
    if (e.key === "End") setPosition(100);
  };

  const checkerStyle = bgChecker
    ? {
        backgroundImage:
          "linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)",
        backgroundSize: "16px 16px",
        backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
        backgroundColor: "#ffffff",
      }
    : { backgroundColor: "#ffffff" };

  return (
    <div
      ref={containerRef}
      data-testid="before-after-slider"
      className={`relative w-full select-none overflow-hidden ${className}`}
      style={{ ...checkerStyle, touchAction: "none" }}
      onPointerDown={onPointerDown}
    >
      {/* BEFORE — fills the box */}
      <img
        src={beforeSrc}
        alt={beforeLabel}
        draggable={false}
        className="block w-full h-auto select-none pointer-events-none"
      />

      {/* AFTER — clipped to the right of position */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 0 0 ${position}%)` }}
      >
        <img
          src={afterSrc}
          alt={afterLabel}
          draggable={false}
          className="block w-full h-auto select-none pointer-events-none"
        />
      </div>

      {/* Labels */}
      <div
        data-testid="slider-before-label"
        className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 bg-black/70 text-white"
      >
        {beforeLabel}
      </div>
      <div
        data-testid="slider-after-label"
        className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 bg-black/70 text-white"
      >
        {afterLabel}
      </div>

      {/* Divider line + handle */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)] pointer-events-none"
        style={{ left: `${position}%` }}
      />
      <button
        type="button"
        role="slider"
        aria-label="Before-after slider handle"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        data-testid="slider-handle"
        onKeyDown={onKey}
        onPointerDown={(e) => { e.stopPropagation(); setDragging(true); }}
        className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white border-2 border-[#ED5A24] shadow-lg flex items-center justify-center cursor-ew-resize transition-transform duration-150 ${
          dragging ? "scale-110" : "hover:scale-110"
        }`}
        style={{ left: `${position}%` }}
      >
        <GripVertical className="w-5 h-5 text-[#ED5A24]" strokeWidth={2.5} />
      </button>
    </div>
  );
}
